import { Table } from 'react-dsgov';
import { IArrematacao, IArrematacaoListagem } from '../../../../../../../interfaces/IArrematacao';
import { BrButton, BrLoading } from '@govbr-ds/react-components';
import styles from './arrematacaoTable.module.scss';
import { formatCurrency } from '../../../../../../../utils/formatCurrency';
import { useContext } from 'react';
import { DiefCfemContext } from '../../../../../../../contexts/DiefCfemContext';
import { formatarNrProcesso } from '../../../../../../../utils/formatNrProcesso';

interface IHeader {
  field: string;
  label: string;
}

interface IProps {
  data: IArrematacaoListagem[];
  headers: IHeader[];
  isLoading: boolean;
  mapArrematacoesToListagem: () => void;
  setEdicaoArrematacao: React.Dispatch<React.SetStateAction<IArrematacao | undefined>>;
  setNovaArrematacao: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ArrematacaoTable = ({
  data,
  headers,
  isLoading,
  mapArrematacoesToListagem,
  setEdicaoArrematacao,
  setNovaArrematacao,
}: IProps) => {
  const context = useContext(DiefCfemContext);
  const declaracaoHastaPublica = context?.declaracaoHastaPublica;

  const excluirArrematacao = (arrematacao: IArrematacao) => {
    //TODO - colocar modal perguntando se quer excluir mesmo
    if (declaracaoHastaPublica) {
      declaracaoHastaPublica.arrematacoes = declaracaoHastaPublica?.arrematacoes?.filter(
        (obj) =>
          !(
            obj.municipio.codigoIBGE === arrematacao.municipio.codigoIBGE &&
            (arrematacao.numeroProcesso ? obj.numeroProcesso === arrematacao.numeroProcesso : true) &&
            obj.substancia === arrematacao.substancia
          )
      );
      mapArrematacoesToListagem();
    }
  };

  const editarArrematacao = (arrematacao: IArrematacao) => {
    setEdicaoArrematacao(arrematacao);
    window.scrollTo(0, 0);
    setNovaArrematacao(true);
  };

  return (
    <div className={styles.customTable}>
      {data.length === 0 && !isLoading ? (
        <Table
          title={'Título - Arrematação em hasta pública'}
          showTopBar={true}
          headers={headers}
          showPagination={false}
          showDensityButtons={false}
          showSearch={false}
          density='medium'
        >
          <tbody className={styles.noRecords}>
            <tr>
              <td colSpan={headers.length}>
                <div>Não há arrematações para serem exibidas.</div>
              </td>
            </tr>
          </tbody>
        </Table>
      ) : (
        data.map((row, index) => (
          <Table
            title={
              row.numeroProcesso === '0'
                ? 'Sem Processo Minerário' + ' | ' + (row.titulo || 'Título - Arrematação em hasta pública')
                : 'Processo Minerário - Nº ' +
                  (formatarNrProcesso(row.numeroProcesso) || '-') +
                  ' | ' +
                  (row.titulo || 'Título - Arrematação em hasta pública')
            }
            showTopBar={true}
            headers={headers}
            showPagination={false}
            showDensityButtons={false}
            showSearch={false}
            density='medium'
            className={styles.arrematacaoTable}
            key={index}
          >
            <tbody key={index}>
              {!row.arrematacoes && !isLoading ? (
                <tr>
                  <td className={styles.noRecords}>
                    <div className={styles.noRecords}>Não há arrematações para serem exibidas.</div>
                  </td>
                </tr>
              ) : (
                Array.isArray(row.arrematacoes) &&
                row.arrematacoes.map((rowArrematacao, index) => (
                  <tr key={index}>
                    {headers.map((header, index) => {
                      if (header?.label === 'Operações')
                        return (
                          <td align='center' key={index}>
                            <div className={styles.statusColumn}>
                              <BrButton
                                secondary
                                circle
                                onClick={() => editarArrematacao(rowArrematacao.arrematacao)}
                                title='Editar'
                              >
                                <i className={`fas fa-edit fa-md  ${styles.columnIcon}`} />
                              </BrButton>
                              <BrButton
                                secondary
                                circle
                                onClick={() => excluirArrematacao(rowArrematacao.arrematacao)}
                                title='Excluir'
                              >
                                <i className={`fas fa-trash-alt fa-md  ${styles.columnIcon}`} />
                              </BrButton>
                            </div>
                          </td>
                        );
                      else if (header?.label === 'Preço Unitário') {
                        return (
                          <td align='center' key={header.field}>
                            {formatCurrency(
                              parseFloat(rowArrematacao.valorArrematacao ?? '') /
                                parseFloat(rowArrematacao.quantidade ?? '')
                            ) || '-'}
                          </td>
                        );
                      } else if (header?.label === 'Valor de Arrematação')
                        return (
                          <td align='center' key={header.field}>
                            {formatCurrency(rowArrematacao[header.field]) || '-'}
                          </td>
                        );
                      else if (header?.label === 'Quantidade')
                        return (
                          <td align='center' key={header.field}>
                            {rowArrematacao[header.field] !== undefined && rowArrematacao[header.field] !== null
                              ? rowArrematacao[header.field].toString().replace('.', ',')
                              : '-'}
                          </td>
                        );
                      else if (header?.label === 'Município/UF') {
                        return (
                          <td align='center' key={header.field}>
                            <div>
                              {rowArrematacao.arrematacao[header.field]
                                ? `${rowArrematacao.arrematacao[header.field].nome}/${
                                    rowArrematacao.arrematacao[header.field].uf
                                  }`
                                : ''}
                            </div>
                          </td>
                        );
                      } else if (
                        header?.label === 'Substância' ||
                        header?.label === 'Unidade' ||
                        header?.label === 'Tipo de Uso'
                      )
                        return (
                          <td align='center' key={header.field}>
                            {rowArrematacao.arrematacao[header.field] || '-'}
                          </td>
                        );
                      else
                        return (
                          <td align='center' key={header.field}>
                            {rowArrematacao[header.field] || '-'}
                          </td>
                        );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        ))
      )}
      {isLoading && (
        <div className={styles.tableLoading}>
          <BrLoading large label='Carregando...' />
        </div>
      )}
    </div>
  );
};
